import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import VerifiedIcon from '@mui/icons-material/Verified';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import './LandingPage.css';

interface LandingPageProps {
  darkMode: boolean;
}

const testimonials = [
  {
    quote: "I referred a friend to a startup and earned $5,000 when she got hired. The whole process was transparent and easy.",
    author: "Sarah M.",
    role: "Software Engineer at Google",
    stars: 5,
  },
  {
    quote: "We filled 3 senior roles through Jobometry referrals in just 2 months. The quality of candidates was outstanding.",
    author: "James K.",
    role: "VP Engineering at a Series B Startup",
    stars: 5,
  },
  {
    quote: "As a recruiter, this platform lets me monetize my network. I have earned over $15K in bounties so far.",
    author: "Priya R.",
    role: "Independent Recruiter",
    stars: 5,
  },
];

const faqs = [
  {
    q: "How does the referral bounty work?",
    a: "When a vacancy poster hires your referred candidate, they pay the bounty through our platform. After a 14-day hold period, the bounty is paid out to you. We charge a 10% platform fee to the poster.",
  },
  {
    q: "Do I need to work at the company to refer someone?",
    a: "No! Anyone can refer candidates to any open vacancy. Verified company employees get a trust badge, but referrals are open to everyone.",
  },
  {
    q: "How do I get verified?",
    a: "Sign up and go to your Profile > Verification tab. Enter your company email, and we will send a verification code. Once verified, you get a trust badge on all your posts.",
  },
  {
    q: "Is there a cost to browse or refer?",
    a: "Browsing vacancies and submitting referrals is completely free. Only vacancy posters pay when they hire a referred candidate.",
  },
  {
    q: "What happens if the hire does not work out?",
    a: "We have a 14-day hold period after the hire is confirmed. If the hire is reversed within this period, the bounty is refunded to the poster.",
  },
];

const LandingPage: React.FC<LandingPageProps> = ({ darkMode }) => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className={`landing-page ${darkMode ? 'dark' : ''}`}>
      {/* Hero Section */}
      <section className="hero">
        <h1>Refer great people.<br />Earn rewards.</h1>
        <p className="hero-subtitle">
          The global referral marketplace where anyone can post vacancies with bounties,
          anyone can refer candidates, and referrers earn when their candidate gets hired.
        </p>
        <div className="hero-ctas">
          <button className="btn-primary btn-lg" onClick={() => navigate('/vacancies')}>
            <SearchIcon sx={{ fontSize: 20 }} /> Browse Vacancies
          </button>
          <button className="btn-secondary btn-lg" onClick={() => navigate('/post-vacancy')}>
            <AddCircleOutlineIcon sx={{ fontSize: 20 }} /> Post a Vacancy
          </button>
        </div>
      </section>

      {/* Company Logos Bar */}
      <section className="company-logos-bar">
        <p className="logos-label">Trusted by employees at leading companies</p>
        <div className="logos-row">
          {['Google', 'Microsoft', 'Stripe', 'Airbnb', 'Uber'].map(name => (
            <div key={name} className="company-logo-item">
              <span>{name}</span>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <h2>How It Works</h2>
        <div className="steps-grid">
          <div className="step-card">
            <div className="step-icon">
              <WorkOutlineIcon sx={{ fontSize: 40 }} />
            </div>
            <div className="step-number">1</div>
            <h3>Post Vacancy with Bounty</h3>
            <p>Companies or hiring managers post open positions with a referral bounty to incentivize great referrals.</p>
          </div>
          <div className="step-card">
            <div className="step-icon">
              <PeopleOutlineIcon sx={{ fontSize: 40 }} />
            </div>
            <div className="step-number">2</div>
            <h3>Browse & Refer</h3>
            <p>Anyone can browse vacancies and refer candidates from their network who would be a great fit.</p>
          </div>
          <div className="step-card">
            <div className="step-icon">
              <AttachMoneyIcon sx={{ fontSize: 40 }} />
            </div>
            <div className="step-number">3</div>
            <h3>Earn When Hired</h3>
            <p>When your referred candidate gets hired, you earn the referral bounty. Simple as that.</p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <h2>What Our Users Say</h2>
        <div className="testimonials-grid">
          {testimonials.map((t, i) => (
            <div key={i} className="testimonial-card">
              <div className="testimonial-stars">
                {Array.from({ length: t.stars }).map((_, j) => (
                  <StarIcon key={j} sx={{ fontSize: 18, color: '#f59e0b' }} />
                ))}
              </div>
              <p className="testimonial-quote">"{t.quote}"</p>
              <div className="testimonial-author">
                <strong>{t.author}</strong>
                <span>{t.role}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust & Verification */}
      <section className="trust-section">
        <div className="trust-content">
          <div className="trust-text">
            <h2>Trust Through Verification</h2>
            <p>
              Verified company employees get trust badges on their vacancy posts,
              giving referrers confidence that the opportunity is legitimate and the
              bounty will be honored.
            </p>
            <div className="trust-badge-demo">
              <VerifiedIcon sx={{ fontSize: 24, color: '#1976d2' }} />
              <span>Verified Employee Badge</span>
            </div>
          </div>
          <div className="trust-stats">
            <div className="stat-box">
              <span className="stat-number">$1K-$10K</span>
              <span className="stat-label">Typical bounty range</span>
            </div>
            <div className="stat-box">
              <span className="stat-number">Global</span>
              <span className="stat-label">Remote & on-site roles</span>
            </div>
            <div className="stat-box">
              <span className="stat-number">Free</span>
              <span className="stat-label">To browse & refer</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-list">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`faq-item ${openFaq === i ? 'open' : ''}`}
            >
              <button
                className="faq-question"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span>{faq.q}</span>
                <ExpandMoreIcon
                  sx={{
                    fontSize: 24,
                    transform: openFaq === i ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                  }}
                />
              </button>
              {openFaq === i && (
                <div className="faq-answer">
                  <p>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2>Ready to start earning?</h2>
        <p>Browse open vacancies and start referring great people today.</p>
        <button className="btn-primary btn-lg" onClick={() => navigate('/vacancies')}>
          Get Started
        </button>
      </section>
    </div>
  );
};

export default LandingPage;
